import React from 'react';
import { Platform, useWindowDimensions } from 'react-native';
import styled from 'styled-components/native';
import { useNavigation } from '@react-navigation/core';
import { StackNavigationProp } from '@react-navigation/stack';
import { MaterialIcons } from '@expo/vector-icons';
import Button from '../../components/Button';
import { B, H2, P } from '../../components/Typography';
import Colors from '../../constants/Colors';
import IconButton from '../../components/IconButton';

const StyledContainer = styled.View`
  background: ${Colors.secondarySand};
  width: 100%;
  height: 100%;
`;

const StyledScrollView = styled.ScrollView`
  width: 100%;
  height: 100%;
  position: absolute;
  z-index: 1;
`;

const StyledView = styled.View`
  width: 100%;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 0 20px;
`;

const StyledContent = styled.View<{ isMobile: boolean }>`
  max-width: ${p => (p.isMobile ? '100%' : '500px')};
`;

const StyledIconView = styled.View<{ isMobileLandscape: boolean }>`
  align-items: center;
  margin-top: ${p => (p.isMobileLandscape ? 30 : 80)}px;
  margin-bottom: ${p => (p.isMobileLandscape ? 30 : 80)}px; ;
`;

const StyledButtonsView = styled.View`
  justify-content: center;
  align-self: center;
  width: 230px;
`;

const StyledBackgroundImageView = styled.Image`
  width: 100%;
  height: 100%;
`;

const StyledIconImage = styled.Image`
  width: 225px;
  height: 225px;
`;

const StyledNextButton = styled(Button)`
  width: 90px;
  height: 34px;
  align-self: center;
  margin-bottom: 25px;
  margin-top: 25px;
`;

const StyledSkipButton = styled(IconButton)`
  position: absolute;
  flex-flow: row;
  align-items: center;
  justify-content: center;
  border-color: transparent;
  height: 18px;
  width: 45px;
  right: 0;
`;

const StyledHeaderText = styled(H2)`
  margin-top: 70px;
  font-size: 39px;
  line-height: 45px;
  letter-spacing: -0.17px;
  text-align: center;
`;

const StyledDefinitionText = styled(P)`
  font-size: 18px;
  letter-spacing: -0.09px;
  text-align: center;
  font-style: italic;
  font-weight: bold;
`;

const StyledInfoText = styled(P)`
  font-size: 18px;
  line-height: 26px;
  letter-spacing: -0.11px;
  text-align: center;
  font-weight: bold;
`;

const StyledSkipText = styled(B)`
  font-size: 13px;
  line-height: 20px;
`;

const IntroAbout1Screen: React.FC = () => {
  const navigation = useNavigation<StackNavigationProp<any>>();
  const window = useWindowDimensions();
  const isMobileLandscape = Platform.OS !== 'web' && window.width >= 800;
  const isMobile = window.width < 500;
  const backgroundAsset = isMobile
    ? require('../../assets/intro/intro-background-start.png')
    : require('../../assets/intro/intro-background-whole.png');

  return (
    <StyledContainer>
      <StyledBackgroundImageView resizeMode='cover' source={backgroundAsset} />
      <StyledScrollView
        scrollIndicatorInsets={{ right: 1 }}
        contentContainerStyle={{
          flexGrow: 1,
          flexDirection: 'column',
          justifyContent: 'space-around'
        }}
      >
        <StyledView>
          <StyledContent isMobile={isMobile}>
            <StyledHeaderText color='dark' padding={3}>
              Welcome to Chloe
            </StyledHeaderText>
            <StyledDefinitionText color='dark'>
              Powered by People Science
            </StyledDefinitionText>
            <StyledIconView isMobileLandscape={isMobileLandscape}>
              <StyledIconImage
                source={require('../../assets/intro/intro-about-1-doctors.png')}
              />
            </StyledIconView>
            <StyledInfoText color='dark' padding={2}>
              Chloe was created by doctors and scientists to help you test which
              alternative medicines work best for you.
            </StyledInfoText>
            <IntroNavigationControls
              onNext={() => navigation.navigate('IntroAbout2Screen')}
              onSkip={() => navigation.navigate('Intro')}
            />
          </StyledContent>
        </StyledView>
      </StyledScrollView>
    </StyledContainer>
  );
};

type IntroNavigationControlsProps = {
  onNext: () => void;
  onSkip?: () => void;
};

export const IntroNavigationControls: React.FC<
  IntroNavigationControlsProps
> = ({ onNext, onSkip }) => (
  <StyledButtonsView>
    <StyledNextButton size='small' onPress={onNext}>
      Next
    </StyledNextButton>
    <StyledSkipButton onPress={onSkip}>
      <StyledSkipText color='dark'>Skip</StyledSkipText>
      <MaterialIcons
        name='keyboard-arrow-right'
        size={20}
        color='black'
        style={{ width: 15 }}
      />
    </StyledSkipButton>
  </StyledButtonsView>
);

export default IntroAbout1Screen;
